import React, {useState, useImperativeHandle, forwardRef} from 'react';
import PhoneInput, {isValidPhoneNumber, Value} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

export type PhoneReceiptInfo = {
  phoneNumber: Value;
};

// Export the ref methods so parent components can access validation
export interface PhoneReceiptFormRef {
  validateAll: () => boolean;
  hasErrors: () => boolean;
  getErrors: () => Partial<Record<keyof PhoneReceiptInfo, string>>;
}

const PhoneReceiptForm = forwardRef<
  PhoneReceiptFormRef,
  {
    phoneReceiptInfo: PhoneReceiptInfo;
    setPhoneReceiptInfo: React.Dispatch<React.SetStateAction<PhoneReceiptInfo>>;
  }
>((props, ref) => {
  const {phoneReceiptInfo, setPhoneReceiptInfo} = props;

  // Internal error state - no longer passed from parent
  const [errors, setErrors] = useState<
    Partial<Record<keyof PhoneReceiptInfo, string>>
  >({});

  // Expose validation methods to parent via ref
  useImperativeHandle(ref, () => ({
    validateAll: () => {
      const newErrors: Partial<Record<keyof PhoneReceiptInfo, string>> = {};
      if (!phoneReceiptInfo.phoneNumber) {
        newErrors.phoneNumber = 'Phone number is required';
      } else {
        if (!isValidPhoneNumber(phoneReceiptInfo.phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid phone number';
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    hasErrors: () => {
      return Object.values(errors).some((error) => error !== '');
    },
    getErrors: () => errors,
  }));

  // Real-time validation for phone number
  const validatePhoneNumber = (value: Value) => {
    if (!value) {
      setErrors((prev) => ({...prev, phoneNumber: ''}));
      return;
    }
    if (!isValidPhoneNumber(value)) {
      setErrors((prev) => ({
        ...prev,
        phoneNumber: 'Please enter a valid phone number',
      }));
      return;
    } else {
      setErrors((prev) => ({...prev, phoneNumber: ''}));
    }
  };

  const handlePhoneNumberChange = (value: Value) => {
    setPhoneReceiptInfo({...phoneReceiptInfo, phoneNumber: value});
    validatePhoneNumber(value);
  };

  return (
    <div className="w-full">
      {errors.phoneNumber && (
        <div className="mt-1 transition-all duration-300 ease-in-out">
          <p className="text-smoothRed text-sm font-medium animate-fade-in">
            {errors.phoneNumber}
          </p>
        </div>
      )}

      <div className="w-full border rounded-md border-subtleGrey">
        <PhoneInput
          defaultCountry="KE"
          countries={['KE']}
          placeholder="Enter Phone Number"
          value={phoneReceiptInfo.phoneNumber}
          onChange={handlePhoneNumberChange}
          className={`
            ${
              errors.phoneNumber
                ? 'text-smoothRed border-pinkNegative border w-full rounded-md bg-surfaceNegative px-4 py-4'
                : 'w-full border-none rounded-md px-4 py-4 text-headlineGrayscale focus:outline-none focus:ring-none focus:border-none bg-transparent'
            }
          `}
          style={{
            '--PhoneInputCountryFlag-height': '1em',
            '--PhoneInputCountrySelectArrow-color': '#6b7280',
            '--PhoneInput--focus': 'transparent',
            '--PhoneInput-border-color--focus': 'transparent',
            '--PhoneInput-outline': 'none',
            '--PhoneInputInput-outline': 'none',
            '--PhoneInputCountrySelect-outline': 'none',
          }}
        />
      </div>
    </div>
  );
});

PhoneReceiptForm.displayName = 'PhoneReceiptForm';

export default PhoneReceiptForm;
